import type { SaveExportPresetInput, UserExportPreset } from '@mss_creator/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { exportPresetsApi } from './export-presets-api';

export const USER_EXPORT_PRESETS_QUERY_KEY = ['export-presets'] as const;

/** Shared so an empty or still-loading list keeps one identity across renders. */
const NO_PRESETS: readonly UserExportPreset[] = [];

export interface UseUserExportPresetsResult {
  presets: readonly UserExportPreset[];
  isError: boolean;
  /** Rejects with the `ApiError` on failure, so the save form can show it. */
  save: (input: SaveExportPresetInput) => Promise<UserExportPreset>;
  /** Resolves once the preset is gone — including when it was already gone (404). */
  remove: (id: string) => Promise<void>;
  isSaving: boolean;
}

/** Inserts the saved preset, or replaces the one with the same name, then re-sorts by name. */
function upsertByName(
  current: readonly UserExportPreset[] | undefined,
  saved: UserExportPreset,
): UserExportPreset[] {
  const others = (current ?? []).filter((preset) => preset.name !== saved.name);
  return [...others, saved].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

/** Deletes a preset, treating « already gone » (a 404 — deleted from another tab) as done. */
async function removePreset(id: string): Promise<void> {
  try {
    await exportPresetsApi.remove(id);
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) return;
    throw cause;
  }
}

/**
 * The signed-in user's personal export presets, plus save and remove. The
 * cache is patched on success so the picker updates at once — a saved preset
 * turns active right away — and a save is then re-fetched to pick up the
 * server's canonical list. A failed remove re-fetches instead, so the row the
 * user sees is the server's.
 */
export function useUserExportPresets(): UseUserExportPresetsResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: USER_EXPORT_PRESETS_QUERY_KEY,
    queryFn: exportPresetsApi.list,
  });

  const saveMutation = useMutation({
    mutationFn: exportPresetsApi.save,
    onSuccess: async (saved) => {
      queryClient.setQueryData<UserExportPreset[]>(USER_EXPORT_PRESETS_QUERY_KEY, (current) =>
        upsertByName(current, saved),
      );
      await queryClient.invalidateQueries({ queryKey: USER_EXPORT_PRESETS_QUERY_KEY });
    },
  });

  const removeMutation = useMutation({
    mutationFn: removePreset,
    // A list refetch already in flight would land after the delete and bring
    // the row back — cancel it; the cache patch below is the fresher state.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: USER_EXPORT_PRESETS_QUERY_KEY });
    },
    onSuccess: (_result, id) => {
      queryClient.setQueryData<UserExportPreset[]>(USER_EXPORT_PRESETS_QUERY_KEY, (current) =>
        (current ?? []).filter((preset) => preset.id !== id),
      );
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: USER_EXPORT_PRESETS_QUERY_KEY });
    },
  });

  return {
    presets: query.data ?? NO_PRESETS,
    isError: query.isError,
    save: saveMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
}
